import { RootState } from '../../../../init-store/index';
import { selectIsCalendarSummarizationReceiveAvailable } from '../../../pages/call/selectors';
import { isRoomAgentMode, selectIsTranslatorIntegration } from '../config/selectors';
import { isAnonymousUser } from '../user/selectors';
import type { BinaryFlags, Flags, ObjectFlags } from './type';

const getFlag = (state: Partial<RootState>, name: keyof Flags, defaultValue?: string) => {
    return (state.flags && state.flags[name]) || defaultValue;
};

export const getBinaryFlag = (state: Partial<RootState>, name: keyof BinaryFlags) => {
    const flag = getFlag(state, name);

    return flag === true || flag === '1';
};

const getObjectFlag = (state: Partial<RootState>, name: keyof ObjectFlags) => {
    const flag = getFlag(state, name);

    if (typeof flag !== 'object') {
        return;
    }

    const result: { [key: string]: boolean } = {};

    for (const [key, value] of Object.entries(flag)) {
        result[key] = value === true || value === 'true' || value === '1';
    }

    return result;
};

export const isLocalRecordingEnabled = (state: Partial<RootState>) => getBinaryFlag(state, 'local_recording_enabled');

export const getGoloomSessionFlags = (state: Partial<RootState>) => getObjectFlag(state, 'goloom_session_flags');

export const getIsSoundProcessingDisabled = (state: RootState) =>
    isRoomAgentMode(state) || getBinaryFlag(state, 'sound_processing_disabled');

export const isMobileVirtualBackgroundEnabled = (state: Partial<RootState>) =>
    getBinaryFlag(state, 'mobile_virtual_background_enabled');

export const selectIsTestplane = ({ flags: { isTestplane } }: RootState) => isTestplane;

export const selectIsOneTimeElementsShownAlways = (state: RootState) =>
    getBinaryFlag(state, 'show_always_one_time_elements');

export const selectToggleNewGrid = (state: RootState) => getBinaryFlag(state, 'toggleNewGrid');

export const selectIsNoiseCancellationFeatureEnabled = (state: RootState) =>
    getBinaryFlag(state, 'is_noise_cancellation_feature_enabled');

export const selectIsCalendarSummarizationFeatureEnabled = (state: RootState) =>
    selectIsCalendarSummarizationReceiveAvailable(state);

export const selectIsUserIODiagnosticsEnabled = (state: RootState) => {
    return (
        (getBinaryFlag(state, 'io_diagnostics_enabled') || getBinaryFlag(state, 'audio_diagnostics')) &&
        !selectIsTestplane(state)
    );
};

export const selectIsDiagnosticsEnabled = (state: RootState) => getBinaryFlag(state, 'diagnostics_enabled');

export const selectIsCorpHeaderEnabled = (state: RootState) => getBinaryFlag(state, 'is_corp_header');

export const selectIsWarmupModeEnabled = (state: Partial<RootState>) => getBinaryFlag(state, 'warmup_mode_enabled');

export const selectIsConferencesHistoryEnabled = (state: RootState) =>
    !selectIsTestplane(state) && getBinaryFlag(state, 'isConferencesHistoryEnabled') && !isAnonymousUser(state);

export const selectIsAccountPreselectEnabled = (state: RootState) => getBinaryFlag(state, 'account_preselect');

export const selectIsEasyAuthEnabled = (state: RootState) => getBinaryFlag(state, 'cookier360_easy_auth');

export const selectIsPaasEnabled = (state: RootState) => getBinaryFlag(state, 'paas_enabled');

export const selectIsPromozavrPdokEnabled = (state: RootState) => getBinaryFlag(state, 'promozavr_pdok_enabled');

export const selectFullHistoryArtifactsEnabled = (state: RootState) => getBinaryFlag(state, 'history_full_artifacts');

export const selectNewNativeAppBannerEnabled = (state: RootState) =>
    getBinaryFlag(state, 'new_native_app_banner') || selectIsTestplane(state);

export const selectIsSipStabilizationFpsEnabled = (state: RootState) =>
    isRoomAgentMode(state) && getBinaryFlag(state, 'sip_stabilization_fps');

export const selectIsTranslatorStabilizationFpsEnabled = (state: RootState) =>
    selectIsTranslatorIntegration(state) && getBinaryFlag(state, 'translator_stabilization_fps');

export const selectIsLocalRecordingStabilizationFpsEnabled = (state: RootState) =>
    !isRoomAgentMode(state) &&
    !selectIsTranslatorIntegration(state) &&
    getBinaryFlag(state, 'local_recording_stabilization_fps');

export const selectIsRecorderOverlayCacheEnabled = (state: RootState) =>
    selectIsSipStabilizationFpsEnabled(state) ||
    selectIsTranslatorStabilizationFpsEnabled(state) ||
    selectIsLocalRecordingStabilizationFpsEnabled(state);

export const selectIsRvfcSkipEnabled = (state: RootState) =>
    selectIsSipStabilizationFpsEnabled(state) ||
    selectIsTranslatorStabilizationFpsEnabled(state) ||
    selectIsLocalRecordingStabilizationFpsEnabled(state);

export const selectIsHeadlessShellEnabled = (state: RootState) =>
    selectIsSipStabilizationFpsEnabled(state) || selectIsTranslatorStabilizationFpsEnabled(state);

export const selectIsHeadlessCustomViewportEnabled = (state: RootState) => selectIsSipStabilizationFpsEnabled(state);

export const selectIsLowMinFrameDurationEnabled = (state: RootState) => selectIsSipStabilizationFpsEnabled(state);

// Включает worker-prescale — off-thread ресайз слотов участников в drawer'е.
export const selectIsWorkerPreScaleEnabled = (state: RootState) => selectIsSipStabilizationFpsEnabled(state);

export const selectIsUncappedRecorderFpsEnabled = (state: RootState) =>
    getBinaryFlag(state, 'enable_uncapped_recorder_fps');

export const selectIsTelemessengerExpEnabled = (state: RootState) => getBinaryFlag(state, 'telemessenger');

export const selectIsSlotPluginsEnabled = (state: RootState) => getBinaryFlag(state, 'slot_plugins_enabled');

export const selectIsDontDropCallOnErrorEnabled = (state: RootState) => getBinaryFlag(state, 'dont_drop_call_on_error');

export const selectIsAudioSessionTypeAutoEnabled = (state: RootState) =>
    getBinaryFlag(state, 'audio_session_type_auto');

export const selectIsToolbarV2Enabled = (state: RootState) => getBinaryFlag(state, 'toolbar_v2');

export const selectIsSharingZoomFlagEnabled = (state: RootState) => getBinaryFlag(state, 'sharing_zoom_enabled');

export const selectIsSharingZoomEnabled = (state: RootState) =>
    selectIsSharingZoomFlagEnabled(state) || state.settings.isSharingZoomOverrideEnabled;
